import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import AppButton from '../app-button';

interface QueryStateProps {
  /** `loading` from the consuming `useQuery` / `use*Query` hook. */
  loading: boolean;
  /** `error` from the consuming hook. `undefined` when the query is healthy. */
  error: ApolloError | undefined;
  /** Optional render while loading. Defaults to rendering nothing, matching most call sites. */
  loadingFallback?: ReactNode;
  /** Apollo's `refetch` from the same hook. Omit to hide the retry button. */
  onRetry?: () => void;
  /** Rendered on the happy path. Optional so the component can stand alone at bail-early sites. */
  children?: ReactNode;
}

/**
 * Inline "couldn't load this section, try again" fallback for query sites where
 * `data: null` would otherwise render an empty area. No-op on the happy path.
 * `onRetry` is usually the hook's `refetch` (Apollo dedupes, so double-click is safe).
 */
const QueryState: React.FC<QueryStateProps> = ({
  loading,
  error,
  loadingFallback,
  onRetry,
  children,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return <>{loadingFallback ?? null}</>;
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX={2}
        paddingY={4}
      >
        <Box marginBottom={2}>
          <Typography variant="body2" align="center">
            {t('errors.section.failed')}
          </Typography>
        </Box>
        {onRetry && (
          <AppButton color="primary" variant="outlined" onClick={() => onRetry()}>
            {t('errors.section.retry')}
          </AppButton>
        )}
      </Box>
    );
  }

  return <>{children ?? null}</>;
};

export default QueryState;
